import React, { useEffect, useRef } from 'react';
import { ArrowUpIcon, CameraIcon, MapPinIcon, MicIcon, SparklesIcon } from 'lucide-react';
interface PromptBoxProps {
  value: string;
  onChange: (value: string) => void;
  onSubmit: () => void;
  /** Bouton photo affiché uniquement si un gestionnaire est fourni. */
  onAddPhoto?: () => void;
  /** `hero` is the single-line pill shown before the conversation starts. */
  variant: 'hero' | 'chat';
  placeholder: string;
  busy?: boolean;
  photoAttached?: boolean;
  autoFocus?: boolean;
}
const toolButton = 'grid h-10 w-10 shrink-0 place-items-center rounded-full text-slate-500 transition-colors duration-150 ease-ville hover:bg-slate-100 hover:text-ville-navy focus:outline-none focus-visible:ring-2 focus-visible:ring-ville-indigo';
export function PromptBox({
  value,
  onChange,
  onSubmit,
  onAddPhoto,
  variant,
  placeholder,
  busy = false,
  photoAttached = false,
  autoFocus = false
}: PromptBoxProps) {
  const areaRef = useRef<HTMLTextAreaElement | null>(null);
  const inputId = variant === 'hero' ? 'demande-input' : 'demande-reponse';
  useEffect(() => {
    const el = areaRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${Math.min(el.scrollHeight, 168)}px`;
  }, [value, variant]);
  useEffect(() => {
    if (!autoFocus) return;
    const timer = window.setTimeout(() => areaRef.current?.focus(), 320);
    return () => window.clearTimeout(timer);
  }, [autoFocus]);
  const send = () => {
    if (busy) return;
    onSubmit();
  };
  if (variant === 'hero') {
    return <form onSubmit={event => {
      event.preventDefault();
      send();
    }} className="flex items-center gap-2 rounded-full border border-slate-200 bg-white py-2.5 pl-5 pr-2.5 shadow-lg shadow-ville-navy/5 transition-colors duration-150 ease-ville focus-within:border-ville-indigo" data-id="element-926" data-mp="1vstofjrh1fre">
        <SparklesIcon className="h-5 w-5 shrink-0 text-ville-indigo" aria-hidden="true" data-id="element-927" data-mp="2elp6rho7svcx" />
        <label htmlFor={inputId} className="sr-only" data-id="element-928" data-mp="1c7zp35mql5xn">
          Que voulez-vous signaler ?
        </label>
        <input id={inputId} type="text" value={value} onChange={event => onChange(event.target.value)} placeholder={placeholder} className="min-w-0 flex-1 border-0 bg-transparent py-2.5 text-base text-ville-navy placeholder:text-slate-400 focus:outline-none sm:text-lg" data-id="element-929" data-mp="1h73zfzled9pk" />
        {onAddPhoto && <button type="button" onClick={onAddPhoto} aria-label="Ajouter une photo" className={toolButton} data-id="element-930" data-mp="1uy8xix949txs">
            <CameraIcon className="h-5 w-5" aria-hidden="true" data-id="element-931" data-mp="1zzojcrj4c4h8" />
          </button>}
        <button type="button" aria-label="Utiliser ma position" className={`hidden sm:grid ${toolButton}`} data-id="element-932" data-mp="24ukei0jnj7yk">
          <MapPinIcon className="h-5 w-5" aria-hidden="true" data-id="element-933" data-mp="28vxn2ie4pp96" />
        </button>
        <button type="button" aria-label="Dicter la description" className={toolButton} data-id="element-934" data-mp="akgsjjk36g9x">
          <MicIcon className="h-5 w-5" aria-hidden="true" data-id="element-935" data-mp="ekr7vmtrqj1g" />
        </button>
        <button type="submit" aria-label="Envoyer la description à ATNA" className="grid h-11 w-11 shrink-0 place-items-center rounded-full bg-ville-lime text-ville-navy transition-transform duration-150 ease-ville hover:-translate-y-0.5 focus:outline-none focus-visible:ring-2 focus-visible:ring-ville-indigo focus-visible:ring-offset-2" data-id="element-936" data-mp="71rqtnts53l9">
          <ArrowUpIcon className="h-5 w-5" aria-hidden="true" data-id="element-937" data-mp="q040rbsimhhw" />
        </button>
      </form>;
  }
  return <form onSubmit={event => {
    event.preventDefault();
    send();
  }} className="rounded-[24px] border border-slate-200 bg-white px-4 pb-3 pt-3.5 transition-colors duration-150 ease-ville focus-within:border-ville-indigo" data-id="element-938" data-mp="l09btdsaw1zh">
      <label htmlFor={inputId} className="sr-only" data-id="element-939" data-mp="c25ms7i3wn2x">
        Répondre à ATNA
      </label>
      <textarea id={inputId} ref={areaRef} rows={1} value={value} onChange={event => onChange(event.target.value)} onKeyDown={event => {
      if (event.key === 'Enter' && !event.shiftKey) {
        event.preventDefault();
        send();
      }
    }} placeholder={placeholder} className="block max-h-[168px] w-full resize-none border-0 bg-transparent px-1 text-base leading-relaxed text-ville-navy placeholder:text-slate-400 focus:outline-none sm:text-[17px]" data-id="element-940" data-mp="31n211g51qreq" />

      <div className="mt-1.5 flex items-center justify-between gap-3" data-id="element-941" data-mp="178wmgjxilvmi">
        <div className="flex items-center gap-0.5" data-id="element-942" data-mp="1c6ob952v51mh">
          {onAddPhoto && <button type="button" onClick={onAddPhoto} aria-label="Joindre une photo" aria-pressed={photoAttached} className={photoAttached ? 'grid h-10 w-10 shrink-0 place-items-center rounded-full bg-ville-indigo-soft text-ville-indigo focus:outline-none focus-visible:ring-2 focus-visible:ring-ville-indigo' : toolButton} data-id="element-943" data-mp="y75gddz2bms4">
              <CameraIcon className="h-5 w-5" aria-hidden="true" data-id="element-944" data-mp="3fdnexiixiuzf" />
            </button>}
          <button type="button" aria-label="Utiliser ma position" className={toolButton} data-id="element-945" data-mp="3bdlh5kgbelpf">
            <MapPinIcon className="h-5 w-5" aria-hidden="true" data-id="element-946" data-mp="3pcv0gszthvxm" />
          </button>
          <button type="button" aria-label="Dicter votre réponse" className={toolButton} data-id="element-947" data-mp="3kfusgcjeqj3v">
            <MicIcon className="h-5 w-5" aria-hidden="true" data-id="element-948" data-mp="2wq0hk0inf4m9" />
          </button>
          {photoAttached && <span className="ml-1 text-xs font-semibold text-ville-indigo" data-id="element-949" data-mp="2rqxakaj3yobn">1 photo jointe</span>}
        </div>

        <button type="submit" disabled={busy || value.trim().length === 0} aria-label="Envoyer à ATNA" className="grid h-11 w-11 shrink-0 place-items-center rounded-full bg-ville-lime text-ville-navy transition-[transform,opacity] duration-150 ease-ville hover:-translate-y-0.5 focus:outline-none focus-visible:ring-2 focus-visible:ring-ville-indigo focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:bg-slate-100 disabled:text-slate-400 disabled:hover:translate-y-0" data-id="element-950" data-mp="36fk0t7a9x0hn">
          <ArrowUpIcon className="h-5 w-5" aria-hidden="true" data-id="element-951" data-mp="ht8exjwl1p44" />
        </button>
      </div>
    </form>;
}